import React from 'react';

import {
  IconAdjustments,
  IconBrain,
  IconClipboardCheck,
  IconEyeEdit,
  IconFolder,
  IconMessages,
  IconPhone,
  IconPhoneIncoming,
  IconSettingsBolt,
  IconSettingsPlus,
  IconShoppingCart,
  IconUserBolt,
  IconUsers,
  IconWand,
  IconWorldUpload
} from '@tabler/icons-react';
import { AdvancedManagementSection } from 'pages/ManageSmartAssistant/Sections/AdvancedManagementSection';
import { ConfigurationManagementSection } from 'pages/ManageSmartAssistant/Sections/ConfigurationManagementSection';
import { ConversationsSection } from 'pages/ManageSmartAssistant/Sections/ConversationSections/ConversationsSection/ConversationsSection';
import { VoiceCallsSection } from 'pages/ManageSmartAssistant/Sections/ConversationSections/VoiceCallsSection/VoiceCallsSection';
import { CustomizationManagementSection } from 'pages/ManageSmartAssistant/Sections/CustomizationManagementSection';
import { EvaluationsSection } from 'pages/ManageSmartAssistant/Sections/EvaluationsSection/EvaluationsSection';
import { LeadsAnalyticsSection } from 'pages/ManageSmartAssistant/Sections/LeadsAnalyticsSection';
import { MemoriesManagementSection } from 'pages/ManageSmartAssistant/Sections/MemoriesManagementSection';
import { MessagingBasicsManagementSection } from 'pages/ManageSmartAssistant/Sections/MessagingBasicsManagementSection';
import { PhoneAgentBasicsManagementSection } from 'pages/ManageSmartAssistant/Sections/PhoneAgentBasicsManagementSection';
import { ResourcesManagementSection } from 'pages/ManageSmartAssistant/Sections/ResourcesManagementSection';
import { StoreManagementSection } from 'pages/ManageSmartAssistant/Sections/StoreManagementSection';
import { TelephonyManagementSection } from 'pages/ManageSmartAssistant/Sections/TelephonyManagementSection';
import { ToolsSection } from 'pages/ManageSmartAssistant/Sections/ToolsManagementSection';
import { WebsiteIntegrationManagementSection } from 'pages/ManageSmartAssistant/Sections/WebsiteIntegrationManagementSection';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { ManageSmartAssistantTabSlug } from 'shared/constants/route-links';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';

export interface ManageSmartAssistantTab {
  component: React.ReactNode;
  icon: React.ReactNode;
  label: string;
  slug: ManageSmartAssistantTabSlug;
}

interface UseManageSmartAssistantTabsConfigurationParams {
  smartAssistant: SmartAssistant;
  smartAssistantAvatar: React.ReactElement;
}

export function useManageSmartAssistantTabsConfiguration({
  smartAssistant,
  smartAssistantAvatar
}: UseManageSmartAssistantTabsConfigurationParams) {
  const location = useLocation();
  const { t } = useTranslation();
  const { conversationId } = queryString.parse(location.search);

  const tabsConfiguration: Record<ManageSmartAssistantTabSlug, ManageSmartAssistantTab> = {
    [ManageSmartAssistantTabSlug.RESOURCES]: {
      component: <ResourcesManagementSection smartAssistant={smartAssistant} />,
      icon: <IconFolder />,
      label: t('SMART_ASSISTANT.RESOURCES_SECTION.TITLE_SHORT'),
      slug: ManageSmartAssistantTabSlug.RESOURCES
    },
    [ManageSmartAssistantTabSlug.CONFIGURATION]: {
      component: <ConfigurationManagementSection smartAssistant={smartAssistant} />,
      icon: <IconAdjustments />,
      label: t('SMART_ASSISTANT.CONFIGURATION_SECTION.TITLE'),
      slug: ManageSmartAssistantTabSlug.CONFIGURATION
    },
    [ManageSmartAssistantTabSlug.CUSTOMIZATION]: {
      component: <CustomizationManagementSection smartAssistant={smartAssistant} />,
      icon: <IconEyeEdit />,
      label: t('SMART_ASSISTANT.CUSTOMIZATION_SECTION.TITLE'),
      slug: ManageSmartAssistantTabSlug.CUSTOMIZATION
    },
    [ManageSmartAssistantTabSlug.STORE]: {
      component: <StoreManagementSection smartAssistant={smartAssistant} />,
      icon: <IconShoppingCart />,
      label: t('SMART_ASSISTANT.STORE_SECTION.TITLE_SHORT'),
      slug: ManageSmartAssistantTabSlug.STORE
    },
    [ManageSmartAssistantTabSlug.MESSAGING_BASICS]: {
      component: <MessagingBasicsManagementSection smartAssistant={smartAssistant} />,
      icon: <IconSettingsBolt />,
      label: t('SMART_ASSISTANT.MESSAGING_BASICS_SECTION.TITLE'),
      slug: ManageSmartAssistantTabSlug.MESSAGING_BASICS
    },
    [ManageSmartAssistantTabSlug.WEBSITE_INTEGRATION]: {
      component: <WebsiteIntegrationManagementSection smartAssistant={smartAssistant} avatar={smartAssistantAvatar} />,
      icon: <IconWorldUpload />,
      label: t('SMART_ASSISTANT.WEBSITE_INTEGRATION_SECTION.TITLE'),
      slug: ManageSmartAssistantTabSlug.WEBSITE_INTEGRATION
    },
    [ManageSmartAssistantTabSlug.TOOLS]: {
      component: <ToolsSection smartAssistant={smartAssistant} />,
      icon: <IconWand />,
      label: t('SMART_ASSISTANT.TOOLS_SECTION.TITLE'),
      slug: ManageSmartAssistantTabSlug.TOOLS
    },
    [ManageSmartAssistantTabSlug.CONVERSATIONS]: {
      component: (
        <ConversationsSection
          smartAssistant={smartAssistant}
          forcedConversationId={typeof conversationId === 'string' ? conversationId : undefined}
        />
      ),
      icon: <IconMessages />,
      label: t('SMART_ASSISTANT.CONVERSATIONS_SECTION.TITLE'),
      slug: ManageSmartAssistantTabSlug.CONVERSATIONS
    },
    [ManageSmartAssistantTabSlug.LEADS]: {
      component: <LeadsAnalyticsSection smartAssistant={smartAssistant} />,
      icon: <IconUsers />,
      label: t('SMART_ASSISTANT.LEADS_SECTION.TITLE'),
      slug: ManageSmartAssistantTabSlug.LEADS
    },
    [ManageSmartAssistantTabSlug.PHONE_AGENT_BASICS]: {
      component: <PhoneAgentBasicsManagementSection smartAssistant={smartAssistant} />,
      icon: <IconUserBolt />,
      label: t('SMART_ASSISTANT.PHONE_AGENT_BASICS_SECTION.TITLE'),
      slug: ManageSmartAssistantTabSlug.PHONE_AGENT_BASICS
    },
    [ManageSmartAssistantTabSlug.TELEPHONY]: {
      component: <TelephonyManagementSection smartAssistant={smartAssistant} />,
      icon: <IconPhone />,
      label: t('SMART_ASSISTANT.TELEPHONY_SECTION.TITLE'),
      slug: ManageSmartAssistantTabSlug.TELEPHONY
    },
    [ManageSmartAssistantTabSlug.VOICE_CALLS]: {
      component: <VoiceCallsSection smartAssistant={smartAssistant} />,
      icon: <IconPhoneIncoming />,
      label: t('SMART_ASSISTANT.VOICE_CALLS_SECTION.TITLE'),
      slug: ManageSmartAssistantTabSlug.VOICE_CALLS
    },
    [ManageSmartAssistantTabSlug.MEMORIES]: {
      component: <MemoriesManagementSection smartAssistant={smartAssistant} />,
      icon: <IconBrain />,
      label: t('SMART_ASSISTANT.MEMORIES_SECTION.TITLE'),
      slug: ManageSmartAssistantTabSlug.MEMORIES
    },
    [ManageSmartAssistantTabSlug.EVALUATIONS]: {
      component: <EvaluationsSection smartAssistant={smartAssistant} />,
      icon: <IconClipboardCheck />,
      label: t('SMART_ASSISTANT.EVALUATIONS_SECTION.TITLE'),
      slug: ManageSmartAssistantTabSlug.EVALUATIONS
    },
    [ManageSmartAssistantTabSlug.ADVANCED]: {
      component: <AdvancedManagementSection smartAssistant={smartAssistant} />,
      icon: <IconSettingsPlus />,
      label: t('SMART_ASSISTANT.ADVANCED_SECTION.TITLE'),
      slug: ManageSmartAssistantTabSlug.ADVANCED
    }
  };

  return tabsConfiguration;
}
